import jwtDecode from "jwt-decode";
import { ACCESS_TOKEN, REFRESH_TOKEN, basePath } from "../config/constants";

/** Margen (s) antes del `exp` en el que se renueva de forma proactiva. */
export const REFRESH_MARGIN_SECONDS = 300;

const readStored = (key) => {
  const value = localStorage.getItem(key);
  return !value || value === "null" ? null : value;
};

/**
 * Access token almacenado mientras NO haya vencido (sin margen).
 *
 * Antes devolvía null cuando faltaban menos de 5 min: con access de 300 días
 * daba igual, pero con access de 1 h dejaba sin Authorization a los llamadores
 * que lo usan "en crudo" justo antes del canje. Ahora un token vigente que se
 * acerca al vencimiento se sigue devolviendo; la renovación anticipada la hace
 * el scheduler (sessionKeeper) y `getValidAccessToken`. Para "vigente con
 * margen" usar `getFreshAccessTokenSync`.
 */
const getAccessTokenApi = () => {
  const accessToken = readStored(ACCESS_TOKEN);
  return accessToken && !willExpireToken(accessToken, 0) ? accessToken : null;
};

/** ¿Hay credenciales guardadas (access o refresh), aunque el access esté vencido? */
const hasSession = () => !!(readStored(ACCESS_TOKEN) || readStored(REFRESH_TOKEN));

/** Access token almacenado solo si le quedan más de 5 min (la semántica previa de getAccessTokenApi). */
const getFreshAccessTokenSync = () => {
  const accessToken = readStored(ACCESS_TOKEN);
  return accessToken && !willExpireToken(accessToken) ? accessToken : null;
};

const getRefreshTokenApi = () => {
  const refreshToken = readStored(REFRESH_TOKEN);
  if (!refreshToken) return null;
  // Un refresh opaco (no JWT) no se puede juzgar localmente: decide el servidor.
  if (!isDecodableJwt(refreshToken)) return refreshToken;
  return willExpireToken(refreshToken) ? null : refreshToken;
};

// Motivo por el que el servidor cerró la sesión, para mostrarlo en /login. Va en
// sessionStorage porque el cierre termina en una recarga completa (window.location.replace,
// o el arranque de la app sin contenedor de toasts montado) y un toast no sobrevive a eso.
const SESSION_END_REASON_KEY = "beartrack.sessionEndReason";

const markSessionEnded = (reason) => {
  try {
    sessionStorage.setItem(SESSION_END_REASON_KEY, reason);
  } catch {
    // Storage no disponible (modo privado): se pierde solo el aviso, no la lógica de sesión.
  }
};

/** Motivo guardado, o null. Solo lectura: es seguro llamarlo desde un render. */
const getSessionEndReason = () => {
  try {
    return sessionStorage.getItem(SESSION_END_REASON_KEY);
  } catch {
    return null;
  }
};

/** Borra el motivo una vez mostrado, para que no reaparezca en el próximo login. */
const clearSessionEndReason = () => {
  try {
    sessionStorage.removeItem(SESSION_END_REASON_KEY);
  } catch {
    // Storage no disponible: nada que borrar.
  }
};

/**
 * Resultado de intentar renovar la sesión. Existen TRES desenlaces y no dos:
 * confundir "el servidor dice que esta credencial está muerta" con "no pude
 * hablar con el servidor" es lo que hace que un 500 o un 429 borren las
 * credenciales de un usuario cuya sesión estaba perfectamente viva.
 */
export const REFRESH_OK = "ok";
/**
 * El servidor rechazó la credencial (401/403, o un 400 de /auth/refresh): la
 * sesión murió de verdad. Puede traer `reason`: "blocked" si el backend
 * responde que el usuario fue bloqueado, "invalid" para cualquier otro 400
 * (el 401/403 no trae `reason`).
 */
export const REFRESH_TERMINAL = "terminal";
/** No hubo veredicto del servidor (5xx, 429, red, respuesta ilegible). */
export const REFRESH_TRANSIENT = "transient";

/**
 * Texto literal con el que el backend (bff-auth-gateway) rechaza el canje de
 * un usuario bloqueado. Es la única señal que el front tiene para distinguir
 * "tu cuenta fue desactivada" de cualquier otro 400.
 */
const BLOCKED_USER_MESSAGE = "The user is blocked, please contact your administrator.";

/**
 * Lee el body de una respuesta sin confiar en ella: `json()` puede no existir
 * o lanzar de forma síncrona, y lo devuelto puede ser null o no ser un objeto.
 * Siempre devuelve un objeto, así que quien la use puede leer campos directo.
 * @returns {Promise<Object>}
 */
async function readBodyAsObject(response) {
  try {
    const body = await response.json();
    return body && typeof body === "object" ? body : {};
  } catch {
    return {};
  }
}

const REFRESH_TIMEOUT_MS = 20000;
const REFRESH_LOCK_NAME = "bt-auth-refresh";

/**
 * Si el almacenamiento ya tiene un refresh DISTINTO del que se mandó, otra
 * pestaña canjeó entretanto: se adopta su resultado en vez de dar la sesión por
 * muerta. Devuelve null si no hay nada que adoptar.
 */
function rotatedByAnotherTab(sentRefreshToken) {
  const storedRefresh = readStored(REFRESH_TOKEN);
  if (!storedRefresh || storedRefresh === sentRefreshToken) return null;
  const storedAccess = readStored(ACCESS_TOKEN);
  if (storedAccess && !willExpireToken(storedAccess, 0)) {
    return { status: REFRESH_OK, accessToken: storedAccess };
  }
  return { status: REFRESH_TRANSIENT };
}

/**
 * Canje real contra el backend. No llamar directo: usar `refreshSession`, que
 * es el que garantiza un solo canje en vuelo.
 */
const performRefresh = async (refreshToken) => {
  const url = `${basePath}/auth/refresh`;
  // Timeout explícito: el canje corre dentro del lock entre pestañas y un fetch
  // colgado lo retendría para todas.
  const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
  const timer = controller ? setTimeout(() => controller.abort(), REFRESH_TIMEOUT_MS) : null;
  const params = {
    method: "POST",
    body: JSON.stringify({ refreshToken }),
    headers: {
      "Content-Type": "application/json",
    },
    ...(controller ? { signal: controller.signal } : {}),
  };

  let response;
  try {
    response = await fetch(url, params);
  } catch {
    // Sin respuesta no hay veredicto: la sesión puede estar viva.
    return { status: REFRESH_TRANSIENT };
  } finally {
    if (timer) clearTimeout(timer);
  }

  if (response.status === 401 || response.status === 403) {
    // Otra pestaña (p. ej. una con la versión anterior de la app, que no usa el
    // lock) pudo rotar el refresh mientras este canje estaba en vuelo: el rechazo
    // es por haber usado uno ya consumido, no un veredicto sobre la sesión.
    const rotated = rotatedByAnotherTab(refreshToken);
    if (rotated) return rotated;
    // `code` (REFRESH_INVALID / REFRESH_REVOKED / REFRESH_REUSED / SESSION_REVOKED)
    // es solo diagnóstico: cualquiera de ellos es terminal.
    const { code } = await readBodyAsObject(response);
    return typeof code === "string" ? { status: REFRESH_TERMINAL, code } : { status: REFRESH_TERMINAL };
  }

  if (response.status === 400) {
    // Un 400 de /auth/refresh es un veredicto del servidor sobre la credencial,
    // no infraestructura: el backend lo usa para rechazar el canje y, en
    // particular, para avisar que el usuario fue bloqueado con la sesión
    // abierta. Tratarlo como transitorio dejaba a ese usuario dentro del panel
    // con todo fallando y sin enterarse de que su cuenta estaba desactivada.
    //
    // Caveat aceptado: el backend lanza ESTE MISMO 400 con ESTE MISMO texto
    // cuando falla la escritura del personal token en BD (bff-auth-gateway,
    // auth.service.ts:413-416). Es indistinguible desde el front, así que se
    // acepta tratarlo también como bloqueo.
    const body = await readBodyAsObject(response);
    const rotated = rotatedByAnotherTab(refreshToken);
    if (rotated) return rotated;
    const reason = body.message === BLOCKED_USER_MESSAGE ? "blocked" : "invalid";
    if (reason === "blocked") markSessionEnded("blocked");
    return { status: REFRESH_TERMINAL, reason };
  }

  if (response.status !== 200) {
    // 5xx, 429 del throttler, 502 del proxy: problema de infraestructura, no
    // de la credencial. Antes todo esto caía en el mismo saco que el 401 y
    // deslogueaba.
    return { status: REFRESH_TRANSIENT };
  }

  let result;
  try {
    result = await response.json();
  } catch {
    return { status: REFRESH_TRANSIENT };
  }

  if (!result?.accessToken || !result?.refreshToken) {
    return { status: REFRESH_TRANSIENT };
  }

  try {
    localStorage.setItem(ACCESS_TOKEN, result.accessToken);
    localStorage.setItem(REFRESH_TOKEN, result.refreshToken);
  } catch {
    // Storage lleno o bloqueado (modo privado): tenemos un par nuevo que no
    // podemos persistir, así que el reintento seguiría mandando el token viejo.
    // Es transitorio, no un veredicto sobre la credencial.
    return { status: REFRESH_TRANSIENT };
  }

  // La sesión sigue viva: un "bloqueo" anterior era el 400 ambiguo del backend, no uno real.
  clearSessionEndReason();
  return { status: REFRESH_OK, accessToken: result.accessToken };
};

/**
 * Canje en vuelo compartido. Es una variable de módulo, o sea UNA POR PESTAÑA:
 * deduplica dentro de la pestaña, no entre pestañas.
 */
let refreshInFlight = null;

/**
 * Canjea el refreshToken y persiste el par nuevo si sale bien.
 *
 * Un solo canje en vuelo aunque N llamadores pidan renovar a la vez: el primero
 * dispara el request y el resto se engancha a la misma promesa. Vive acá y no
 * en el interceptor porque el interceptor no es el único que renueva —
 * `aiChatService` y el arranque de `authProviders` también lo hacen, y con el
 * mutex del lado del interceptor esos dos quedaban afuera y podían canjear en
 * paralelo. Con rotación de refresh token del lado del servidor, dos canjes
 * concurrentes se invalidan entre sí.
 *
 * NUNCA rechaza: todos los desenlaces se expresan en `status`. Sólo
 * `REFRESH_TERMINAL` justifica destruir la sesión.
 */
const refreshSession = (refreshToken) => {
  if (!refreshInFlight) {
    refreshInFlight = withCrossTabLock(() => refreshInsideLock(refreshToken)).finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
};

/**
 * Ejecuta `task` en exclusión mutua entre TODAS las pestañas del origen (Web
 * Locks). Con refresh rotativo de un solo uso, dos pestañas canjeando el mismo
 * refresh a la vez harían que la segunda llegue con uno ya consumido y el
 * backend revoque la sesión. Sin Web Locks (navegadores viejos, jsdom) queda
 * solo el single-flight por pestaña de `refreshSession`.
 */
async function withCrossTabLock(task) {
  const locks = typeof navigator !== "undefined" ? navigator.locks : undefined;
  if (!locks || typeof locks.request !== "function") return task();
  try {
    return await locks.request(REFRESH_LOCK_NAME, task);
  } catch (err) {
    if (err && err.name === "SecurityError") return task(); // origen sin acceso a locks
    // `task` nunca rechaza (performRefresh expresa todo en `status`): un error
    // acá es del propio lock y no es veredicto del servidor.
    return { status: REFRESH_TRANSIENT };
  }
}

/**
 * Dentro del lock se relee el almacenamiento: si otra pestaña ya renovó mientras
 * esta esperaba el lock, se usa su resultado y NO se canjea (el refresh que traía
 * este llamador ya está consumido).
 */
async function refreshInsideLock(refreshToken) {
  const adopted = rotatedByAnotherTab(refreshToken);
  if (adopted && adopted.status === REFRESH_OK) return adopted;
  const latest = readStored(REFRESH_TOKEN) || refreshToken;
  return performRefresh(latest);
}

/**
 * Contrato histórico: devuelve el accessToken nuevo o `null`.
 *
 * Se conserva para los llamadores que sólo necesitan el token. La diferencia
 * con la versión anterior es que ya NO borra las credenciales ante un fallo
 * transitorio: sólo ante un rechazo explícito del servidor.
 */
const refreshAccessToken = (refreshToken) =>
  refreshSession(refreshToken).then(({ status, accessToken }) => {
    if (status === REFRESH_OK) return accessToken;
    if (status === REFRESH_TERMINAL) logout();
    return null;
  });

const logout = () => {
  localStorage.removeItem(ACCESS_TOKEN);
  localStorage.removeItem(REFRESH_TOKEN);
};

function isDecodableJwt(token) {
  try {
    jwtDecode(token);
    return true;
  } catch {
    return false;
  }
}

/** Segundos epoch del `exp` del access almacenado, o null si no hay/ no se puede leer. */
const getAccessTokenExpiry = () => {
  const token = readStored(ACCESS_TOKEN);
  if (!token) return null;
  try {
    const { exp } = jwtDecode(token);
    return typeof exp === "number" ? exp : null;
  } catch {
    return null;
  }
};

/**
 * Garantiza un access vigente (con margen de 5 min) y devuelve el DESENLACE
 * completo, para quien necesite distinguir terminal de transitorio. Si el
 * almacenado sirve no toca la red; si no, canjea (bajo el lock entre pestañas).
 * No cierra sesión ni redirige: eso lo decide el llamador.
 * @returns {Promise<{status: string, accessToken: (string|null), reason?: string, code?: string}>}
 */
const ensureSession = async () => {
  const fresh = getFreshAccessTokenSync();
  if (fresh) return { status: REFRESH_OK, accessToken: fresh };

  const refreshToken = getRefreshTokenApi();
  if (!refreshToken) {
    // Sin refresh utilizable: solo sirve el access si todavía no venció.
    const alive = getAccessTokenApi();
    return alive ? { status: REFRESH_OK, accessToken: alive } : { status: REFRESH_TERMINAL, accessToken: null };
  }

  const result = await refreshSession(refreshToken);
  if (result.status === REFRESH_OK) return result;
  // Transitorio: si el access aún no venció sigue siendo usable; el servidor decide.
  const stillAlive = result.status === REFRESH_TRANSIENT ? getAccessTokenApi() : null;
  return { ...result, accessToken: stillAlive };
};

/**
 * Devuelve un access token vigente, renovándolo si hace falta, o null si no hay
 * forma de obtener uno (sin sesión, rechazo terminal, o fallo transitorio con el
 * access ya vencido). Nunca rechaza.
 */
const getValidAccessToken = async () => (await ensureSession()).accessToken;

/**
 * Tras un 401 con `sentAccessToken`: obtiene un access distinto del rechazado.
 * Si otra pestaña/llamador ya lo rotó lo devuelve sin canjear; si no, canjea.
 * @returns {Promise<{status: string, accessToken: (string|null), reason?: string}>}
 */
const refreshAfterUnauthorized = async (sentAccessToken) => {
  const stored = readStored(ACCESS_TOKEN);
  if (stored && stored !== sentAccessToken && !willExpireToken(stored, 0)) {
    return { status: REFRESH_OK, accessToken: stored };
  }
  const refreshToken = getRefreshTokenApi();
  if (!refreshToken) return { status: REFRESH_TERMINAL, accessToken: null };
  const result = await refreshSession(refreshToken);
  return { ...result, accessToken: result.status === REFRESH_OK ? result.accessToken : null };
};

function willExpireToken(token, bufferTimeInSeconds = REFRESH_MARGIN_SECONDS) {
  // try/catch: esta función ahora corre en la ruta crítica del interceptor 401
  // (session401.js) y jwtDecode LANZA ante un token corrupto — sin esto, un
  // token basura en localStorage rompería todo el manejo global de errores.
  try {
    const { exp } = jwtDecode(token);
    const now = Math.floor(Date.now() / 1000);
    return now > exp - bufferTimeInSeconds;
  } catch {
    return true; // token corrupto/no decodificable = tratarlo como vencido
  }
}

export const AuthService = {
  getAccessTokenApi,
  hasSession,
  getFreshAccessTokenSync,
  getAccessTokenExpiry,
  getRefreshTokenApi,
  getValidAccessToken,
  ensureSession,
  refreshAfterUnauthorized,
  refreshAccessToken,
  refreshSession,
  logout,
  getSessionEndReason,
  clearSessionEndReason,
};
